import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as LucideIcons from "lucide-react";
import { LogOut, Menu, X, Sparkles, ArrowUpRight } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useCapability } from "../useCapability";
import { SidebarPrefsControls, useSidebarPrefs } from "../../components/layout/SidebarPrefs";
import { LaunchGuidedDemoButton } from "../demo/LaunchGuidedDemoButton";
import { DemoModeToggle } from "../demoMode/DemoModeUI";

/**
 * Adaptive Sidebar — pure capability-map-driven navigation.
 *
 * Rules (design_guidelines.md §3.1, architecture L1/L2/L9):
 *   * Every nav item resolves through `capability_map.visible_modules`.
 *     Hardcoded role checks are forbidden.
 *   * Modules without a `primary_path` are not navigable from here.
 *   * Items not in the map are completely absent from the DOM (L9).
 *   * The list is filtered to `navigation.sidebar=true` per module
 *     declaration in the registry.
 *
 * This is the new shell sidebar — it lives only inside the Operational
 * Base shell. The legacy sidebar in `/components/layout/Sidebar.jsx`
 * continues to serve the rest of the app while existing routes migrate.
 */
export const AdaptiveSidebar = () => {
    const { user, logout } = useAuth();
    const { map, loading } = useCapability();
    const { t } = useTranslation();
    const [mobileOpen, setMobileOpen] = useState(false);

    const sidebarModules = (map?.visible_modules || []).filter(
        (m) => m.sidebar && m.primary_path,
    );
    // CUSTOMIZE AND MY TABS — the same controls and the same saved setting as
    // the main sidebar (components/layout/SidebarPrefs). A module hidden here
    // is hidden by that one personal setting; nothing is deleted, no link and
    // no permission changes, and the Command Center is never hideable.
    const prefs = useSidebarPrefs();
    const customizable = sidebarModules
        .filter((m) => m.primary_path !== '/command-center')
        .map((m) => ({
            id: `ob:${m.name}`,
            label: m.display_name?.[map?.language || "en"] || m.name,
        }));
    const shown = (list) => list.filter((m) => m.primary_path === '/command-center'
        || !prefs.isHidden(`ob:${m.name}`));

    // v4.2.2 — Honest Transition grouping.
    const nativeModules = shown(sidebarModules.filter(
        (m) => (m.experience || "legacy") === "native",
    ));
    const legacyModules = shown(sidebarModules.filter(
        (m) => (m.experience || "legacy") === "legacy",
    ));

    const onLogout = () => {
        logout();
        window.location.href = "/login";
    };

    const handleNavClick = () => setMobileOpen(false);

    // When the operator clicks a LEGACY item, drop a sessionStorage
    // breadcrumb so the legacy page can render the calm
    // "← Back to Operational Base" banner. Reload-safe — we keep it
    // until the operator returns or starts a new session.
    const markLegacyDeparture = (mod) => {
        try {
            sessionStorage.setItem(
                "ob.legacy_arrival",
                JSON.stringify({
                    from: "operational-base",
                    module: mod.name,
                    display_name: mod.display_name?.[map?.language || "en"] || mod.name,
                    arrived_at: Date.now(),
                }),
            );
        } catch (_e) { /* sessionStorage may be unavailable in strict modes */ }
        handleNavClick();
    };

    const SidebarBody = (
        <div className="h-full flex flex-col" style={{ background: "rgba(11, 16, 24, 0.95)" }}>
            {/* Brand */}
            <div className="px-6 py-7 border-b border-white/5">
                <p className="ob-eyebrow">{t('nav.yourEyebrow')}</p>
                <h1
                    className="text-xl mt-1 flex items-center gap-2"
                    style={{ color: "var(--ob-text-primary)" }}
                >
                    {t('nav.operationalBase')}
                    <Sparkles size={14} strokeWidth={1.5} className="text-emerald-400 opacity-80" aria-hidden="true" />
                </h1>
            </div>

            {/* Navigation — strictly module-derived (L1).
                v4.2.2 — Honest Transition grouping. Native modules first,
                then a clearly-labeled "Classic tools" section so the
                operator never feels yanked between shells. */}
            <nav className="flex-1 overflow-y-auto px-3 py-5">
                {loading && (
                    <p
                        className="px-3 text-sm"
                        style={{ color: "var(--ob-text-tertiary)" }}
                        data-testid="ob-sidebar-loading"
                    >
                        Loading your environment…
                    </p>
                )}
                {!loading && sidebarModules.length === 0 && (
                    <p
                        className="px-3 text-sm"
                        style={{ color: "var(--ob-text-tertiary)" }}
                        data-testid="ob-sidebar-empty"
                    >
                        Your shell is calm right now — no modules are enabled.
                    </p>
                )}

                {/* v4.8.2 — Guided Demo launcher. Self-gates: renders
                    only when Master Demo Mode is active. */}
                <LaunchGuidedDemoButton />

                {/* Native section */}
                {nativeModules.length > 0 && (
                    <div data-testid="ob-sidebar-section-native" className="space-y-1 mb-6">
                        <p
                            className="px-3 mb-2 ob-eyebrow"
                            data-testid="ob-sidebar-section-native-label"
                        >
                            {t("ob.sidebar.section_native")}
                        </p>
                        {nativeModules.map((mod) => (
                            <SidebarItem
                                key={mod.name}
                                moduleName={mod.name}
                                path={mod.primary_path}
                                label={mod.display_name?.[map?.language || "en"] || mod.name}
                                icon={mod.icon}
                                onClick={handleNavClick}
                            />
                        ))}
                    </div>
                )}

                {/* Legacy section */}
                {legacyModules.length > 0 && (
                    <div data-testid="ob-sidebar-section-legacy" className="space-y-1">
                        <div className="px-3 mb-2 flex items-center justify-between">
                            <p
                                className="ob-eyebrow"
                                data-testid="ob-sidebar-section-legacy-label"
                            >
                                {t("ob.sidebar.section_legacy")}
                            </p>
                            <span
                                className="text-[10px] tracking-wider uppercase opacity-60"
                                title={t("ob.sidebar.section_legacy_hint")}
                                style={{ color: "var(--ob-text-tertiary)" }}
                            >
                                {t("ob.sidebar.section_legacy_chip")}
                            </span>
                        </div>
                        {legacyModules.map((mod) => (
                            <SidebarItem
                                key={mod.name}
                                moduleName={mod.name}
                                path={mod.primary_path}
                                label={mod.display_name?.[map?.language || "en"] || mod.name}
                                icon={mod.icon}
                                isLegacy
                                onClick={() => markLegacyDeparture(mod)}
                                tooltip={t("ob.sidebar.opens_in_classic")}
                            />
                        ))}
                    </div>
                )}

                {/* The same Customize / Show All · My Tabs controls as the main
                    sidebar, from the same component. */}
                <SidebarPrefsControls items={customizable} prefs={prefs} tone="dark" />
            </nav>

            {/* User footer */}
            <div className="px-3 py-4 border-t border-white/5">
                <div className="flex items-center gap-3 px-3 py-2">
                    <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm"
                        style={{
                            background: "rgba(16, 185, 129, 0.12)",
                            color: "var(--ob-emerald)",
                        }}
                    >
                        {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p
                            className="text-sm truncate"
                            style={{ color: "var(--ob-text-primary)" }}
                        >
                            {user?.name || "You"}
                        </p>
                        <p
                            className="ob-eyebrow mt-0.5"
                            data-testid="ob-sidebar-role"
                        >
                            {humanizeRole(map?.role || user?.role)}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    data-testid="ob-sidebar-logout"
                    onClick={onLogout}
                    className="mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors"
                    style={{ color: "var(--ob-text-secondary)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.04)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                    <LogOut size={16} strokeWidth={1.5} />
                    Log out
                </button>

                {/* Demo Mode toggle (renders only for users with can_access_demo_mode). */}
                <div className="mt-3">
                    <DemoModeToggle />
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Mobile trigger */}
            <button
                type="button"
                data-testid="ob-sidebar-mobile-toggle"
                onClick={() => setMobileOpen(true)}
                className="lg:hidden fixed top-4 left-4 z-40 w-11 h-11 rounded-xl ob-card flex items-center justify-center"
                aria-label="Open navigation"
            >
                <Menu size={20} strokeWidth={1.5} style={{ color: "var(--ob-text-primary)" }} />
            </button>

            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    role="presentation"
                    className="lg:hidden fixed inset-0 z-40"
                    style={{ background: "var(--ob-overlay)" }}
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Mobile drawer */}
            <aside
                data-testid="ob-sidebar-mobile"
                className={`lg:hidden fixed top-0 left-0 h-full w-72 z-50 transform transition-transform duration-300 ease-out ${
                    mobileOpen ? "translate-x-0" : "-translate-x-full"
                }`}
            >
                <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="absolute top-4 right-4 z-10 p-2 rounded-lg"
                    aria-label="Close navigation"
                    style={{ color: "var(--ob-text-secondary)" }}
                >
                    <X size={20} strokeWidth={1.5} />
                </button>
                {SidebarBody}
            </aside>

            {/* Desktop fixed sidebar */}
            <aside
                data-testid="ob-sidebar-desktop"
                className="hidden lg:block fixed top-0 left-0 h-full w-64 z-30 border-r border-white/5"
            >
                {SidebarBody}
            </aside>
        </>
    );
};

const SidebarItem = ({ moduleName, path, label, icon, onClick, isLegacy, tooltip }) => {
    const Icon = LucideIcons[_iconName(icon)] || LucideIcons.Circle;
    return (
        <NavLink
            to={path}
            onClick={onClick}
            data-testid={`ob-sidebar-nav-${moduleName}`}
            data-experience={isLegacy ? "legacy" : "native"}
            title={tooltip || undefined}
            className={({ isActive }) => {
                const base = "group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors";
                return isActive
                    ? `${base} bg-emerald-500/10 text-white`
                    : `${base} hover:bg-white/5`;
            }}
            style={({ isActive }) => ({
                color: isActive
                    ? "var(--ob-text-primary)"
                    : isLegacy
                    ? "var(--ob-text-tertiary)"
                    : "var(--ob-text-secondary)",
            })}
        >
            <Icon size={18} strokeWidth={1.5} aria-hidden="true" />
            <span className="truncate flex-1">{label}</span>
            {isLegacy && (
                <ArrowUpRight
                    size={12}
                    strokeWidth={1.5}
                    aria-hidden="true"
                    className="opacity-60 group-hover:opacity-100 transition-opacity"
                    data-testid={`ob-sidebar-nav-${moduleName}-legacy-glyph`}
                />
            )}
        </NavLink>
    );
};

function _iconName(name) {
    if (!name) return "Circle";
    return name
        .split("-")
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join("");
}

function humanizeRole(role) {
    if (!role) return "";
    return role
        .split("_")
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(" ");
}
